import React, { useState } from "react";
import { Table, Input, Button, Space, Popconfirm, Typography } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

export interface OptionItem {
  value: string;
  label: string;
}

interface EditableOptionTableProps {
  options: OptionItem[];
  setOptions: (opts: OptionItem[]) => void;
}

const EditableOptionTable: React.FC<EditableOptionTableProps> = ({
  options,
  setOptions,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedLabel, setEditedLabel] = useState("");
  const [search, setSearch] = useState("");

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = (id: string) => {
    setOptions(
      options.map((item) =>
        item.value === id ? { ...item, label: editedLabel } : item
      )
    );
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    setOptions(options.filter((opt) => opt.value !== id));
  };

  const handleAdd = () => {
    const newId = Date.now().toString();
    setOptions([{ value: newId, label: "" }, ...options]);
    setEditingId(newId);
    setEditedLabel("");
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (_: any, record: OptionItem) =>
        editingId === record.value ? (
          <Input
            value={editedLabel}
            onChange={(e) => setEditedLabel(e.target.value)}
            autoFocus
            onPressEnter={() => {
              if (editedLabel.trim()) {
                handleSave(record.value);
              }
            }}
          />
        ) : (
          <Text>{record.label}</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: OptionItem) =>
        editingId === record.value ? (
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => handleSave(record.value)}
              disabled={!editedLabel.trim()}
            />
            <Button
              icon={<CloseOutlined />}
              onClick={() => {
                const isNew =
                  options.find((opt) => opt.value === editingId)?.label === "";
                if (isNew) {
                  setOptions(options.filter((opt) => opt.value !== editingId));
                }
                setEditingId(null);
              }}
            />
          </Space>
        ) : (
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setEditingId(record.value);
                setEditedLabel(record.label);
              }}
            />
            <Popconfirm
              title="Delete this option?"
              onConfirm={() => handleDelete(record.value)}
            >
              <Button icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h3 style={{ margin: 0 }}>Options</h3>
        <Button icon={<PlusOutlined />} onClick={handleAdd} type="primary">
          Add Option
        </Button>
      </div>

      <Input
        placeholder="Search options..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 16, width: "60%" }}
      />

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 5 }}
        size="middle"
      />
    </div>
  );
};

export default EditableOptionTable;
